import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "~/app/store";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { ACTIVE_QUOTE_PAGE, CART_PAGE } from "~/utils/url/staticUrls";
import { minicartApi } from "~/features/cart/minicart/minicartApi";
import { CLEAR_CART } from "~/features/cart/cart/redux/actions/actionTypes";

export interface MinicartStore {
  show: boolean;
  count: number;
  priceValue?: number;
  currencyIso?: string;
  url: string;
  projectionVersion: number;
  activeCartId?: string;
  latestRequestId?: string;
}

const initialState: MinicartStore = {
  show: false,
  count: 0,
  url: userIsSalesman() ? ACTIVE_QUOTE_PAGE : CART_PAGE,
  projectionVersion: 0,
};

const minicartSlice = createSlice({
  name: "minicart",
  initialState,
  reducers: {
    invalidateMinicartProjection: (state) => {
      state.count = 0;
      delete state.priceValue;
      delete state.currencyIso;
      state.show = false;
      state.projectionVersion += 1;
      delete state.activeCartId;
      delete state.latestRequestId;
    },
    applyMergeCartSuccess: (state, action: PayloadAction<number>) => {
      state.count += Math.max(0, action.payload);
      delete state.priceValue;
      delete state.currencyIso;
      state.show = true;
    },
    replaceMinicartProjection: (state, action: PayloadAction<number>) => {
      state.count = Math.max(0, action.payload);
      delete state.priceValue;
      delete state.currencyIso;
      state.show = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(CLEAR_CART, (state) => {
        state.count = 0;
        delete state.priceValue;
        delete state.currencyIso;
        state.show = true;
        state.projectionVersion += 1;
        delete state.activeCartId;
        delete state.latestRequestId;
      })
      .addMatcher(minicartApi.endpoints.getMinicart.matchPending, (state, action) => {
        const { activeCartId, projectionVersion } = action.meta.arg.originalArgs;
        if (projectionVersion !== state.projectionVersion) {
          return;
        }
        state.activeCartId = activeCartId;
        state.latestRequestId = action.meta.requestId;
      })
      .addMatcher(minicartApi.endpoints.getMinicart.matchFulfilled, (state, action) => {
        const { activeCartId, projectionVersion } = action.meta.arg.originalArgs;
        if (
          projectionVersion !== state.projectionVersion ||
          activeCartId !== state.activeCartId ||
          action.meta.requestId !== state.latestRequestId
        ) {
          return;
        }
        state.count = action.payload.miniCartCount;
        state.priceValue = action.payload.priceValue;
        state.currencyIso = action.payload.currencyIso;
        state.show = true;
      });
  },
});

export const { applyMergeCartSuccess, invalidateMinicartProjection, replaceMinicartProjection } = minicartSlice.actions;

export default minicartSlice.reducer;

export const minicartReducer = minicartSlice.reducer;

export const selectMinicartData = (state: RootState) => state.minicart;
export const selectIsEmptyCart = (state: RootState) => state.minicart.count == 0;
export const selectMinicartProjectionVersion = (state: RootState) => state.minicart.projectionVersion;
